import { useState } from 'react';
import type { Script, ScriptOp } from '@reelmachine/core';
import type { ChatProposal } from '../../api/client';
import { useApplyProposal, useChatMessages, useChatProposal, useReel, useSendChat, useUndoChat } from '../../api/hooks';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';

export const SUGGESTIONS = ['Hook shorter', 'More casual', 'Stronger CTA'] as const;

const FIELD_LABEL = { setText: 'spoken', setOnScreen: 'on screen', setVisual: 'visual', setBeat: 'beat' } as const;
const FIELD = { setText: 'text', setOnScreen: 'onScreen', setVisual: 'visual', setBeat: 'beat' } as const;

/** One proposed op in plain words: where, what it says now, what the assistant suggests. */
export function describeProposalOp(script: Script, op: ScriptOp): { where: string; now?: string; next?: string } {
  const lineName = (lineId: string) => {
    const i = script.lines.findIndex((l) => l.id === lineId);
    return i === 0 ? 'Hook' : i > 0 ? `Line ${i + 1}` : 'A removed line';
  };
  switch (op.op) {
    case 'setText':
    case 'setOnScreen':
    case 'setVisual':
    case 'setBeat': {
      const line = script.lines.find((l) => l.id === op.lineId);
      const now = line?.[FIELD[op.op]];
      return { where: `${lineName(op.lineId)}, ${FIELD_LABEL[op.op]}`, ...(now ? { now } : {}), next: op.value };
    }
    case 'setTitle':
      return { where: 'Title', now: script.title, next: op.value };
    case 'insertLine':
      return { where: `New line ${op.index + 1}`, next: op.line.text };
    case 'removeLine': {
      const line = script.lines.find((l) => l.id === op.lineId);
      return { where: `Remove ${lineName(op.lineId).toLowerCase()}`, ...(line ? { now: line.text } : {}) };
    }
    case 'moveLine':
      return { where: `Move ${lineName(op.lineId).toLowerCase()} to line ${op.toIndex + 1}` };
  }
}

function ProposalCard({
  proposal,
  script,
  busy,
  onApply,
  onDismiss,
}: {
  proposal: ChatProposal;
  script: Script;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  return (
    <section className="card proposal" aria-label="Suggested change">
      <h3>Suggested change</h3>
      <p className="small muted">You edited this while I was thinking. Your text stays until you apply mine.</p>
      <ul className="diff-list">
        {proposal.ops.map((op, i) => {
          const d = describeProposalOp(script, op);
          return (
            <li key={i}>
              <span className="field-l">{d.where}</span>
              {d.now !== undefined && (
                <del>
                  <span className="visually-hidden">Now: </span>
                  {d.now}
                </del>
              )}
              {d.next !== undefined && (
                <ins>
                  <span className="visually-hidden">Suggested: </span>
                  {d.next}
                </ins>
              )}
            </li>
          );
        })}
      </ul>
      <div className="row nowrap">
        <button type="button" className="btn sec" disabled={busy} onClick={onDismiss}>
          Keep mine
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onApply}>
          Apply suggestion
        </button>
      </div>
    </section>
  );
}

interface Props {
  reelId: string;
  readOnly: boolean;
  /** Save pending edits before the assistant changes the script. */
  beforeSend: () => Promise<void>;
  /** Called with `change.changed` keys after a reply that changed the script. */
  onChanged: (changed: string[]) => void;
}

export function ChatTab({ reelId, readOnly, beforeSend, onChanged }: Props) {
  const toast = useToast();
  const messages = useChatMessages(reelId);
  const send = useSendChat(reelId);
  const undo = useUndoChat(reelId);
  const script = useReel(reelId).data?.script;
  const { proposal, dismiss } = useChatProposal(reelId);
  const apply = useApplyProposal(reelId);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (text: string) => {
    const message = text.trim();
    if (!message || send.isPending) return;
    setError(null);
    try {
      await beforeSend();
      const res = await send.mutateAsync(message);
      setInput('');
      const reply = res.messages.find((m) => m.role === 'assistant');
      // With a proposal the person decides here first; otherwise jump to the changed fields.
      if (reply?.change?.changed.length && !res.proposal) onChanged(reply.change.changed);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'The assistant did not answer. Try again.');
    }
  };

  const runUndo = async (messageId: string) => {
    setError(null);
    try {
      await undo.mutateAsync(messageId);
      toast('Undone');
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Could not undo.');
    }
  };

  const applyProposal = async () => {
    if (!proposal || !script) return;
    setError(null);
    try {
      await beforeSend();
      const res = await apply.mutateAsync({ script, ops: proposal.ops });
      toast('Suggestion applied');
      if (res.changed.length) onChanged(res.changed);
    } catch (e) {
      setError(
        e instanceof Error && e.message ? `Could not apply the suggestion. ${e.message}` : 'Could not apply it.',
      );
    }
  };

  return (
    <>
      <div className="chat" aria-live="polite">
        <div className="msg ai">Tell me what to change. I mark what I changed.</div>
        {messages.data?.map((m) => (
          <div key={m.id} className={`msg ${m.role === 'user' ? 'me' : 'ai'}`}>
            {m.text}
            {m.change && !m.change.undone && !readOnly && (
              <div className="act">
                <button type="button" disabled={undo.isPending} onClick={() => void runUndo(m.id)}>
                  Undo
                </button>
              </div>
            )}
          </div>
        ))}
        {send.isPending && (
          <div className="msg ai muted" role="status">
            Thinking…
          </div>
        )}
      </div>
      {proposal && script && !readOnly && (
        <ProposalCard
          proposal={proposal}
          script={script}
          busy={apply.isPending}
          onApply={() => void applyProposal()}
          onDismiss={dismiss}
        />
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {!readOnly && (
        <>
          <div className="sugg" role="group" aria-label="Suggestions">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" disabled={send.isPending} onClick={() => void submit(s)}>
                {s}
              </button>
            ))}
          </div>
          <form
            className="row nowrap"
            onSubmit={(e) => {
              e.preventDefault();
              void submit(input);
            }}
          >
            <input
              type="text"
              aria-label="Message"
              placeholder="Change the script…"
              maxLength={2000}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button type="submit" className="iconbtn" aria-label="Send" disabled={send.isPending || !input.trim()}>
              <Icon name="send" />
            </button>
          </form>
        </>
      )}
    </>
  );
}
